
import * as THREE from "../../../libs/three.js/build/three.module.js";

import {t} from "../../digitization/i18n/index.js";
// Resolves an IFC model's true global placement, independent of whatever
// (possibly locally-rebased) coordinates web-ifc's own geometry pipeline
// produces - see loadIfcFile in IFCModule.js for why that distinction
// matters. Reads the raw IFC entity graph directly (GetLine/
// GetLineIDsWithType), in priority order:
//
//   1. IfcMapConversion + IfcProjectedCRS, when both are present - the
//      IFC4-native way to keep small "local engineering" coordinates in the
//      file itself while still declaring a real map/survey position for
//      them, via a separate Eastings/Northings/OrthogonalHeight/rotation/
//      scale transform layered on top of the ordinary placement hierarchy.
//   2. IfcSite.ObjectPlacement's full IfcLocalPlacement hierarchy, when it
//      already encodes a real-world-scale position - the common case for
//      the Danish utility/BIM exports this app otherwise deals with, which
//      usually just place IfcSite directly at its true easting/northing/
//      elevation without bothering with IfcMapConversion at all.
//   3. IfcSite.RefLatitude/RefLongitude/RefElevation, projected to
//      EPSG:25832 (ETRS89 / UTM zone 32N), when neither of the above gives
//      a usable position.
//   4. A logged default assumption (EPSG:25832 horizontally, DVR90
//      vertically) when the file gives no georeferencing information at
//      all - the file's own (possibly arbitrary/local) coordinates are then
//      used as-is, just labelled under that assumption.
//
// Works for IFC2X3 as well as IFC4: IfcMapConversion/IfcProjectedCRS simply
// don't exist as entity types in IFC2X3, so GetLineIDsWithType for them
// always comes back empty there and step 1 is skipped naturally.

const DEFAULT_CRS_LABEL = "EPSG:25832 (ETRS89 / UTM zone 32N)";
const DEFAULT_VERTICAL_DATUM = "DVR90";

// Site placements bigger than this are treated as "already a real survey
// position" (case 2); smaller ones fall through to the RefLatitude/
// RefLongitude fallback (case 3) or the default assumption (case 4). 1km
// comfortably separates "arbitrary small project-local origin" from "a
// genuine EPSG:25832 easting/northing" (always hundreds of thousands to a
// few million, for anywhere in Denmark).
const REAL_WORLD_MAGNITUDE_THRESHOLD = 1000;

const SI_PREFIX_SCALE = {
	EXA: 1e18, PETA: 1e15, TERA: 1e12, GIGA: 1e9, MEGA: 1e6, KILO: 1e3, HECTO: 1e2, DECA: 1e1,
	DECI: 1e-1, CENTI: 1e-2, MILLI: 1e-3, MICRO: 1e-6, NANO: 1e-9, PICO: 1e-12, FEMTO: 1e-15, ATTO: 1e-18,
};

// web-ifc's GetLine() wraps simple IFC values inconsistently depending on
// their defined type: IfcLabel/IfcIdentifier/enums come back as {value},
// while IfcReal/IfcLengthMeasure/etc come back as {_representationValue}
// instead (no .value key at all) - shapes confirmed empirically against
// real project files during development. This normalizes both (and passes
// arrays/plain values through unchanged) so callers never need to care
// which shape a particular attribute happened to use.
function unwrap(x){
	if(x === null || x === undefined){
		return null;
	}
	if(Array.isArray(x)){
		return x.map(unwrap);
	}
	if(typeof x === "object"){
		if("value" in x){
			return unwrap(x.value);
		}
		if("_representationValue" in x){
			return x._representationValue;
		}
	}
	return x;
}

function vec3From(directionRatios, fallback){
	if(!directionRatios){
		return fallback.slice();
	}
	let v = unwrap(directionRatios);
	while(v.length < 3){
		v.push(0);
	}
	return v;
}

function length3(v){
	return Math.hypot(v[0], v[1], v[2]);
}

function normalize3(v){
	let len = length3(v);
	return len > 1e-12 ? [v[0]/len, v[1]/len, v[2]/len] : v;
}

function sub3(a, b){ return [a[0]-b[0], a[1]-b[1], a[2]-b[2]]; }
function dot3(a, b){ return a[0]*b[0] + a[1]*b[1] + a[2]*b[2]; }
function scale3(a, s){ return [a[0]*s, a[1]*s, a[2]*s]; }
function cross3(a, b){
	return [
		a[1]*b[2] - a[2]*b[1],
		a[2]*b[0] - a[0]*b[2],
		a[0]*b[1] - a[1]*b[0],
	];
}

// Builds the 4x4 matrix an IfcAxis2Placement3D represents, in metres
// (locationMeters must already have the length-unit scale applied - Axis/
// RefDirection are unit direction ratios, not lengths, so they need no
// scaling of their own).
function axisPlacementToMatrix(placement, locationMeters){
	let zAxis = placement.Axis ? normalize3(vec3From(placement.Axis.DirectionRatios, [0,0,1])) : [0,0,1];
	let xHint = placement.RefDirection ? vec3From(placement.RefDirection.DirectionRatios, [1,0,0]) : [1,0,0];

	// Gram-Schmidt: RefDirection is only a *hint* for local X - IFC defines
	// IfcAxis2Placement3D's actual X axis as RefDirection projected
	// perpendicular to Axis.
	let xAxis = normalize3(sub3(xHint, scale3(zAxis, dot3(xHint, zAxis))));
	if(length3(xAxis) < 1e-6){
		// RefDirection was parallel to Axis (degenerate or absent) - fall
		// back to an arbitrary but consistent perpendicular.
		xAxis = normalize3(cross3([0,1,0], zAxis));
		if(length3(xAxis) < 1e-6){
			xAxis = normalize3(cross3([1,0,0], zAxis));
		}
	}
	let yAxis = cross3(zAxis, xAxis);

	let m = new THREE.Matrix4();
	m.set(
		xAxis[0], yAxis[0], zAxis[0], locationMeters[0],
		xAxis[1], yAxis[1], zAxis[1], locationMeters[1],
		xAxis[2], yAxis[2], zAxis[2], locationMeters[2],
		0, 0, 0, 1,
	);
	return m;
}

// Recursively composes an IfcLocalPlacement's full PlacementRelTo chain
// ("Hele placeringshierarkiet via IfcLocalPlacement") into one absolute
// matrix, in metres. `localPlacement` must be a flatten=true GetLine()
// result, so PlacementRelTo/RelativePlacement and everything under them are
// already resolved inline rather than left as bare REFs.
function composeLocalPlacement(localPlacement, lengthScale){
	let rel = localPlacement.RelativePlacement;
	let location = rel && rel.Location
		? unwrap(rel.Location.Coordinates).map(c => (c || 0) * lengthScale)
		: [0, 0, 0];
	while(location.length < 3){
		location.push(0);
	}

	let ownMatrix = axisPlacementToMatrix(rel || {}, location);

	if(localPlacement.PlacementRelTo){
		let parentMatrix = composeLocalPlacement(localPlacement.PlacementRelTo, lengthScale);
		return parentMatrix.multiply(ownMatrix);
	}

	return ownMatrix;
}

// IfcCompoundPlaneAngleMeasure: [degrees, minutes, seconds, millionths-of-
// seconds], sign carried on the degrees component only (IFC2X3 and IFC4
// agree on this format for IfcSite.RefLatitude/RefLongitude).
function compoundAngleToDecimalDegrees(compound){
	let [deg, min = 0, sec = 0, micro = 0] = compound;
	let sign = deg < 0 ? -1 : 1;
	return sign * (Math.abs(deg) + min/60 + sec/3600 + Math.abs(micro)/3600e6);
}

// Forward Transverse Mercator (Snyder 1987's closed-form series - the basis
// every UTM implementation, proj4/GDAL included, is ultimately built on),
// parameterized for ETRS89/GRS80 and hardcoded to zone 32N since that's the
// CRS this app assumes throughout (see LER.js: every layer in this app
// treats EPSG:25832 as the working horizontal CRS). Accurate to well under
// a millimetre anywhere within a single UTM zone's width.
function latLonToUTM32N(latDeg, lonDeg){
	const a = 6378137.0; // GRS80 semi-major axis
	const f = 1 / 298.257222101; // GRS80 flattening
	const k0 = 0.9996;
	const zone = 32;

	const e2 = f * (2 - f);
	const ep2 = e2 / (1 - e2);

	const lat = latDeg * Math.PI / 180;
	const lon = lonDeg * Math.PI / 180;
	const lon0 = ((zone - 1) * 6 - 180 + 3) * Math.PI / 180;

	const sinLat = Math.sin(lat);
	const cosLat = Math.cos(lat);
	const tanLat = Math.tan(lat);

	const N = a / Math.sqrt(1 - e2 * sinLat * sinLat);
	const T = tanLat * tanLat;
	const C = ep2 * cosLat * cosLat;
	const A = (lon - lon0) * cosLat;

	const M = a * (
		(1 - e2/4 - 3*e2*e2/64 - 5*e2*e2*e2/256) * lat
		- (3*e2/8 + 3*e2*e2/32 + 45*e2*e2*e2/1024) * Math.sin(2*lat)
		+ (15*e2*e2/256 + 45*e2*e2*e2/1024) * Math.sin(4*lat)
		- (35*e2*e2*e2/3072) * Math.sin(6*lat)
	);

	let easting = k0 * N * (
		A + (1 - T + C) * Math.pow(A, 3) / 6
		+ (5 - 18*T + T*T + 72*C - 58*ep2) * Math.pow(A, 5) / 120
	) + 500000;

	let northing = k0 * (
		M + N * tanLat * (
			A*A/2 + (5 - T + 9*C + 4*C*C) * Math.pow(A, 4) / 24
			+ (61 - 58*T + T*T + 600*C - 330*ep2) * Math.pow(A, 6) / 720
		)
	);

	return {easting, northing};
}

// LengthUnit's scale factor to metres (handles an IfcSIUnit Prefix like
// MILLI, or a derived IfcConversionBasedUnit - e.g. FOOT - via its own
// ConversionFactor chain). "IFC-filens længdeenhed skal respekteres":
// GetLine() returns every measure in the file's own declared unit, so every
// raw coordinate this module reads (site placement, IfcMapConversion's
// Eastings/Northings/OrthogonalHeight) needs this scale applied explicitly.
// Unrelated to web-ifc's own LoadAllGeometry/flatTransformation output,
// which already normalizes to metres internally on its own.
function resolveLengthUnitScale(api, modelID, WebIFC){
	let unitAssignmentIds = [...api.GetLineIDsWithType(modelID, WebIFC.IFCUNITASSIGNMENT)];
	if(unitAssignmentIds.length === 0){
		return {scale: 1, label: t("meter (antaget - ingen IfcUnitAssignment fundet)")};
	}

	let assignment = api.GetLine(modelID, unitAssignmentIds[0], true);
	let lengthUnit = (assignment.Units || []).find(u => unwrap(u.UnitType) === "LENGTHUNIT");
	if(!lengthUnit){
		return {scale: 1, label: t("meter (antaget - intet LENGTHUNIT fundet)")};
	}

	if(lengthUnit.type === WebIFC.IFCSIUNIT){
		let prefix = unwrap(lengthUnit.Prefix);
		let scale = prefix ? (SI_PREFIX_SCALE[prefix] ?? 1) : 1;
		let name = unwrap(lengthUnit.Name) || t("METRE");
		return {scale, label: prefix ? `${prefix} ${name}` : name};
	}

	if(lengthUnit.type === WebIFC.IFCCONVERSIONBASEDUNIT && lengthUnit.ConversionFactor){
		let factor = unwrap(lengthUnit.ConversionFactor.ValueComponent) ?? 1;
		let unitComponent = lengthUnit.ConversionFactor.UnitComponent;
		let baseScale = 1;
		if(unitComponent && unitComponent.type === WebIFC.IFCSIUNIT){
			let prefix = unwrap(unitComponent.Prefix);
			baseScale = prefix ? (SI_PREFIX_SCALE[prefix] ?? 1) : 1;
		}
		let name = unwrap(lengthUnit.Name) || "?";
		return {scale: factor * baseScale, label: name};
	}

	return {scale: 1, label: t("meter (antaget - ukendt enhedstype)")};
}

// See file header for the full priority order. Returns:
//   matrix: THREE.Matrix4 in Potree's Z-up world frame (X=easting,
//           Y=northing, Z=elevation) - meant to be applied *before* the
//           fixed Y-up -> Z-up axis correction loadIfcFile already applies
//           to every model: world = georeference.matrix * axisCorrection *
//           meshLocalMatrix.
//   source, crsLabel, verticalDatum: machine-readable summary of where the
//           georeferencing came from, for the console log the acceptance
//           criteria ask for.
//   lines: that same log, pre-formatted as human-readable strings.
export function resolveIfcGeoreference(api, modelID, WebIFC){
	let lines = [];
	let schema = api.GetModelSchema(modelID);
	lines.push(`IFC-skema: ${schema}`);

	let {scale: lengthScale, label: unitLabel} = resolveLengthUnitScale(api, modelID, WebIFC);
	lines.push(t("Længdeenhed: {0} (× {1} = meter)", unitLabel, lengthScale));

	let crsLabel = null;
	let verticalDatum = null;
	let projectedCrsIds = [...api.GetLineIDsWithType(modelID, WebIFC.IFCPROJECTEDCRS)];
	if(projectedCrsIds.length > 0){
		let crs = api.GetLine(modelID, projectedCrsIds[0], true);
		crsLabel = unwrap(crs.Name) || unwrap(crs.GeodeticDatum);
		verticalDatum = unwrap(crs.VerticalDatum);
	}

	// 1. IfcMapConversion + IfcProjectedCRS
	let mapConversionIds = [...api.GetLineIDsWithType(modelID, WebIFC.IFCMAPCONVERSION)];
	if(mapConversionIds.length > 0 && projectedCrsIds.length > 0){
		let mc = api.GetLine(modelID, mapConversionIds[0], true);
		let eastings = (unwrap(mc.Eastings) ?? 0) * lengthScale;
		let northings = (unwrap(mc.Northings) ?? 0) * lengthScale;
		let height = (unwrap(mc.OrthogonalHeight) ?? 0) * lengthScale;
		let xAbscissa = unwrap(mc.XAxisAbscissa) ?? 1;
		let xOrdinate = unwrap(mc.XAxisOrdinate) ?? 0;
		let mapScale = unwrap(mc.Scale) ?? 1;
		let angle = Math.atan2(xOrdinate, xAbscissa);

		let matrix = new THREE.Matrix4();
		matrix.makeRotationZ(angle);
		matrix.scale(new THREE.Vector3(mapScale, mapScale, mapScale));
		matrix.setPosition(eastings, northings, height);

		lines.push(t(`Georeferering: IfcMapConversion (kilde: IfcMapConversion/IfcProjectedCRS)`));
		lines.push(`  Translation: (${eastings.toFixed(3)}, ${northings.toFixed(3)}, ${height.toFixed(3)}) m`);
		lines.push(`  Rotation: ${(angle * 180 / Math.PI).toFixed(4)}° om Z, skala: ${mapScale}`);

		return {
			matrix, source: "IfcMapConversion",
			crsLabel: crsLabel || DEFAULT_CRS_LABEL,
			verticalDatum: verticalDatum || DEFAULT_VERTICAL_DATUM,
			lines,
		};
	}

	// 2 & 3: IfcSite.ObjectPlacement, and its RefLatitude/RefLongitude as a
	// fallback if that placement turns out not to carry a real position.
	let siteIds = [...api.GetLineIDsWithType(modelID, WebIFC.IFCSITE)];
	if(siteIds.length > 1){
		lines.push(t("Bemærk: {0} IfcSite fundet i filen - bruger den første.", siteIds.length));
	}

	if(siteIds.length > 0){
		let site = api.GetLine(modelID, siteIds[0], true);

		if(site.ObjectPlacement){
			let siteMatrix = composeLocalPlacement(site.ObjectPlacement, lengthScale);
			let position = new THREE.Vector3().setFromMatrixPosition(siteMatrix);

			if(position.length() > REAL_WORLD_MAGNITUDE_THRESHOLD){
				lines.push(t(`Georeferering: IfcSite.ObjectPlacement (kilde: IfcSite, hele IfcLocalPlacement-hierarkiet)`));
				lines.push(t("  Translation: ({0}, {1}, {2}) m - allerede indeholdt i elementernes egen geometri, ingen ekstra forskydning påført.", position.x.toFixed(3), position.y.toFixed(3), position.z.toFixed(3)));

				return {
					matrix: new THREE.Matrix4(), // identity - already baked into every element's own placement
					source: "IfcSite.ObjectPlacement",
					crsLabel: crsLabel || DEFAULT_CRS_LABEL,
					verticalDatum: verticalDatum || DEFAULT_VERTICAL_DATUM,
					lines,
				};
			}
		}

		if(site.RefLatitude && site.RefLongitude){
			let lat = compoundAngleToDecimalDegrees(unwrap(site.RefLatitude));
			let lon = compoundAngleToDecimalDegrees(unwrap(site.RefLongitude));
			let elevation = site.RefElevation ? (unwrap(site.RefElevation) ?? 0) : 0;
			let {easting, northing} = latLonToUTM32N(lat, lon);

			let matrix = new THREE.Matrix4().makeTranslation(easting, northing, elevation);

			lines.push(t(`Georeferering: IfcSite.RefLatitude/RefLongitude/RefElevation (kilde: IfcSite - IfcLocalPlacement gav ingen brugbar global position)`));
			lines.push(t("  Lat/lon: {0}°, {1}° -> projiceret til EPSG:25832: ({2}, {3}, {4}) m", lat.toFixed(6), lon.toFixed(6), easting.toFixed(3), northing.toFixed(3), elevation));
			lines.push(t(`  Ingen rotationsoplysning tilgængelig fra RefLatitude/RefLongitude - antager 0° rotation.`));

			return {
				matrix,
				source: "IfcSite.RefLatitude/RefLongitude",
				crsLabel: crsLabel || DEFAULT_CRS_LABEL,
				verticalDatum: verticalDatum || DEFAULT_VERTICAL_DATUM,
				lines,
			};
		}
	}

	// 4. Nothing usable found - default assumption, geometry used as-is.
	lines.push(t("Ingen georeferering fundet i filen - antager {0} / {1} og bruger filens egne koordinater uændret.", DEFAULT_CRS_LABEL, DEFAULT_VERTICAL_DATUM));

	return {
		matrix: new THREE.Matrix4(),
		source: t("default (ingen georeferering fundet i filen)"),
		crsLabel: DEFAULT_CRS_LABEL,
		verticalDatum: DEFAULT_VERTICAL_DATUM,
		lines,
	};
}
